import React, { useMemo } from 'react';

import { Box } from '@wds/box';
import { t } from '@sm/intl';

import { getCoreCostFromSkus } from '~app/pages/Pricing/lib/lib-helpers';
import { logAmplitudeEvent } from '~app/pages/Pricing/pages/shared';
import { usePricingState } from '~shared/hooks/usePricingState';
import { Packages } from '~shared/constants/pricing';

import useTrackingDetails from '~pricing/hooks/useTrackingDetails';
import ACTIONS from '~shared/actions';
import { AMPLITUDE_PAGE_EVENTS } from '~shared/util';
import { CombinedActions } from '~shared/types/pricing/actions';
import { Grow2529ToggleProps } from '~pricing/experiments/grow_2529_forms/types';
import { PACKAGE_SWAP } from '~pricing/experiments/grow_2529_forms/constants';

import billingToggleCopy from './billingToggleCopy';
import useStyles from './useStyles';

export default function ToggleSwitch({ packageName }: Readonly<Grow2529ToggleProps>): JSX.Element {
  const {
    state: {
      packages: { allPackages },
      grow2529: { toggleChecked },
    },
    dispatch,
  } = usePricingState();
  const { GROW_2529_Switch, Values, Checked } = useStyles();
  const { pageName } = useTrackingDetails();

  function handleChange(): void {
    dispatch({
      type: ACTIONS.FORMS_TOGGLE_SWITCH,
      payload: {
        packageToBeReplaced: packageName,
        replacedWith: PACKAGE_SWAP[packageName as keyof typeof PACKAGE_SWAP].swap.packageName,
      },
    } as CombinedActions);

    const packageSwap = {
      packageToBeReplaced: PACKAGE_SWAP[packageName as keyof typeof PACKAGE_SWAP].id,
      replacedWith: PACKAGE_SWAP[packageName as keyof typeof PACKAGE_SWAP].swap.id,
    };
    dispatch({ type: ACTIONS.SWITCH_PACKAGES, payload: packageSwap } as CombinedActions);

    logAmplitudeEvent({
      eventName: 'pricing clicked toggle',
      pageName,
      amplitudeEvent: AMPLITUDE_PAGE_EVENTS.experimentEvent,
      otherData: {
        selectedPackage: PACKAGE_SWAP[packageName as keyof typeof PACKAGE_SWAP].swap.packageName,
      },
    });
  }
  const savingsPercentage = useMemo(() => {
    let annualPackage = null;
    let monthlyPackage = null;

    switch (packageName) {
      case Packages.FormsAnnual:
      case Packages.FormsMonthly:
        annualPackage = allPackages.find(pkg => pkg.packageName === Packages.FormsAnnual);
        monthlyPackage = allPackages.find(pkg => pkg.packageName === Packages.FormsMonthly);
        break;

      case Packages.StarterAnnual:
      case Packages.StandardMonthly:
        annualPackage = allPackages.find(pkg => pkg.packageName === Packages.StarterAnnual);
        monthlyPackage = allPackages.find(pkg => pkg.packageName === Packages.StandardMonthly);
        break;
    }

    let percentage = 0;

    if (annualPackage && monthlyPackage) {
      const annualSkuCost = getCoreCostFromSkus(annualPackage.skuCost);
      const monthlySkuCost = getCoreCostFromSkus(monthlyPackage.skuCost);

      if (annualSkuCost && monthlySkuCost) {
        percentage = (annualSkuCost.cost / (monthlySkuCost.cost * 12)) * 100 - 100;
      }
    }

    return Math.floor(-percentage);
  }, [allPackages, packageName]);

  const isChecked =
    toggleChecked?.includes(packageName) &&
    (packageName === Packages.StarterAnnual || packageName === Packages.FormsAnnual);

  const id = `${packageName}-toggle`;
  const annualSavings = savingsPercentage ? `(-${savingsPercentage}%)` : null;

  return (
    <label htmlFor={id}>
      <Box className={GROW_2529_Switch}>
        <input
          role="switch"
          aria-checked="false"
          id={id}
          tabIndex={0}
          type="checkbox"
          defaultChecked={isChecked}
          onChange={handleChange}
        />
        <Box className={`${Values} ${isChecked ? Checked : ''}`}>
          <span>{t(billingToggleCopy.grow_2529_monthly_billing)}</span>
          <span>{t(billingToggleCopy.grow_2529_annual_billing, { percentage: annualSavings })}</span>
        </Box>
      </Box>
    </label>
  );
}
